/**
 * Black Friday Weekend — day-switching engine.
 *
 * All "what day/state is it" logic lives here, client-side, using the
 * visitor's Europe/London wall-clock time via Intl — never Liquid, since
 * Shopify caches rendered HTML and a Liquid `now` can go stale.
 *
 * Every day's copy (headline, details, button, gone label, bar/widget text,
 * lightbox content) is already server-rendered into the page by Liquid for
 * every phase — this script never writes copy into the DOM. Its jobs are:
 * decide which pre-rendered phase/state is the right one right now, toggle
 * data-state/data-bf-phase attributes so CSS shows the right thing, and
 * fill in the handful of numbers that genuinely can't be pre-rendered
 * (countdown clocks).
 *
 * Preview override: append ?bf_preview=2026-11-28T10:00 to any URL to make
 * the whole engine behave as if it were that UK wall-clock moment. It's
 * remembered in sessionStorage so it survives clicking through to other
 * pages for the rest of the visit — ?bf_preview=off clears it. sessionStorage
 * is per-tab and never sent to the server, so this can only ever affect the
 * browser that set it, never real customers.
 */
(function () {
  'use strict';

  var DAY_ORDER = ['fri', 'sat', 'sun', 'mon'];
  var TICK_MS = 1000;
  var PREVIEW_STORAGE_KEY = 'bfPreviewOverride';
  var COLLAPSED_STORAGE_KEY = 'bfWidgetCollapsed';

  function pad(n, len) {
    n = String(n);
    while (n.length < len) n = '0' + n;
    return n;
  }

  // Normalizes "2026-11-27", "2026-11-27T00:00" or "2026-11-27T00:00:00"
  // into a fixed-width "YYYY-MM-DDTHH:MM:SS" string, so two of these can be
  // compared with plain < / > / === and get the right chronological answer
  // with no Date math or timezone conversion involved.
  function toComparable(dateStr) {
    if (!dateStr) return null;
    var s = String(dateStr).trim();
    if (s.indexOf('T') === -1) s += 'T00:00:00';
    var bits = s.split('T');
    var timeSegs = bits[1].split(':');
    while (timeSegs.length < 3) timeSegs.push('00');
    return bits[0] + 'T' + timeSegs.map(function (seg) { return pad(seg, 2); }).join(':');
  }

  // Resolves the active preview override (URL wins and persists to
  // sessionStorage for subsequent pages; otherwise falls back to whatever
  // was already stored this session). Returns null when no preview is
  // active, and the literal string "off" support clears it.
  function resolvePreviewOverride() {
    var raw = null;
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.has('bf_preview')) {
        var value = params.get('bf_preview');
        if (value === 'off' || value === '') {
          sessionStorage.removeItem(PREVIEW_STORAGE_KEY);
          return null;
        }
        sessionStorage.setItem(PREVIEW_STORAGE_KEY, value);
        raw = value;
      } else {
        raw = sessionStorage.getItem(PREVIEW_STORAGE_KEY);
      }
    } catch (e) {
      return null;
    }
    return raw ? { raw: raw, comparable: toComparable(raw) } : null;
  }

  function londonNowComparable(preview) {
    if (preview) return preview.comparable;

    var fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    var parts = {};
    fmt.formatToParts(new Date()).forEach(function (p) {
      if (p.type !== 'literal') parts[p.type] = p.value;
    });
    // Some engines report midnight as hour "24" when hour12 is false.
    var hour = parts.hour === '24' ? '00' : parts.hour;
    return parts.year + '-' + parts.month + '-' + parts.day + 'T' + hour + ':' + parts.minute + ':' + parts.second;
  }

  // Treats a "YYYY-MM-DDTHH:MM:SS" wall-clock string as if it were UTC,
  // purely so two of them can be subtracted to get a millisecond delta.
  // Neither side is ever a real UTC/epoch value, so the fictional
  // timezone cancels out and the delta is correct wall-clock elapsed time.
  function wallClockToPseudoEpoch(comparable) {
    var y = +comparable.slice(0, 4);
    var mo = +comparable.slice(5, 7);
    var d = +comparable.slice(8, 10);
    var h = +comparable.slice(11, 13);
    var mi = +comparable.slice(14, 16);
    var s = +comparable.slice(17, 19);
    return Date.UTC(y, mo - 1, d, h, mi, s);
  }

  function formatCountdown(deltaMs) {
    if (deltaMs <= 0) return '00:00:00';
    var totalSeconds = Math.floor(deltaMs / 1000);
    var days = Math.floor(totalSeconds / 86400);
    var hours = Math.floor((totalSeconds % 86400) / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;
    var clock = pad(hours, 2) + ':' + pad(minutes, 2) + ':' + pad(seconds, 2);
    return days > 0 ? days + 'd ' + clock : clock;
  }

  function countdownDelta(nowComparable, targetComparable) {
    if (!targetComparable) return 0;
    return wallClockToPseudoEpoch(targetComparable) - wallClockToPseudoEpoch(nowComparable);
  }

  // The storefront's cookie-consent banner (bottom-left, like the widget)
  // must win while a visitor genuinely has an unanswered choice in front of
  // them — but testing this third-party banner directly (clicking its own
  // accept button, and separately its close icon) found it can keep
  // reporting itself as visible in the DOM indefinitely, even immediately
  // after being "answered" in the same tab, with no detectable class/
  // storage change to key off. Blocking the widget forever on a signal
  // that can't be trusted to ever clear would be worse than the bug this
  // was meant to fix, so this is a bounded wait: block for the first
  // COOKIE_BANNER_GRACE_MS of genuinely continuous visibility (long enough
  // for a real visitor to notice and act on a freshly-shown banner), then
  // stop blocking regardless — covering "already consented", "no banner
  // ever shown", AND "this particular banner never clears itself" in one
  // mechanism, without needing to know which case is true.
  //
  // That wait is also remembered in sessionStorage for the rest of the
  // visit (COOKIE_BANNER_CLEARED_KEY) — otherwise a visitor who already
  // sat through it once on page 1 would be made to wait again on every
  // single page they click through to, which doesn't match "already
  // consented ... must still appear".
  var COOKIE_BANNER_GRACE_MS = 10000;
  var COOKIE_BANNER_CLEARED_KEY = 'bfCookieBannerCleared';
  var cookieBannerFirstSeenAt = null;

  function cookieBannerStatus() {
    try {
      if (sessionStorage.getItem(COOKIE_BANNER_CLEARED_KEY) === '1') {
        return { visible: false, blocking: false };
      }
    } catch (e) {}

    var el = document.querySelector('.cb-cookie_consent-container');
    var visible = false;
    if (el) {
      var style = getComputedStyle(el);
      if (style.display !== 'none' && style.visibility !== 'hidden' && parseFloat(style.opacity) !== 0) {
        var rect = el.getBoundingClientRect();
        visible = rect.width > 0 && rect.height > 0;
      }
    }
    if (!visible) {
      cookieBannerFirstSeenAt = null;
      try {
        sessionStorage.setItem(COOKIE_BANNER_CLEARED_KEY, '1');
      } catch (e) {}
      return { visible: false, blocking: false };
    }
    var now = Date.now();
    if (cookieBannerFirstSeenAt === null) cookieBannerFirstSeenAt = now;
    var blocking = now - cookieBannerFirstSeenAt < COOKIE_BANNER_GRACE_MS;
    if (!blocking) {
      try {
        sessionStorage.setItem(COOKIE_BANNER_CLEARED_KEY, '1');
      } catch (e) {}
    }
    return { visible: true, blocking: blocking };
  }

  function readConfig() {
    var el = document.querySelector('[data-bf-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // "2026-11-23", "2026-11-23T00:00" or "2026-11-23T00:00:00" shaped —
  // loose on purpose (toComparable already tolerates the first two), just
  // enough to reject blank/garbage merchant input before it reaches there.
  var DATE_SHAPE_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?)?$/;

  function isValidDateShape(raw) {
    return !!raw && DATE_SHAPE_RE.test(String(raw).trim());
  }

  // Computes which phase we're in, each day's own state, and the relevant
  // countdown targets. Pure function of (config, now) — no DOM reads.
  function computeState(config, nowComparable) {
    var dayByKey = config.days.reduce(function (acc, d) {
      acc[d.key] = d;
      return acc;
    }, {});

    var teaserStart = toComparable(config.teaserStart);
    var weekendEnd = toComparable(config.weekendEnd);
    var dayStarts = {};
    DAY_ORDER.forEach(function (key) {
      dayStarts[key] = toComparable(dayByKey[key].date);
    });

    // Doors/widget appear partway through the teaser, not at its start —
    // see sections/bf-advent-doors.liquid's own doc comment. Safety
    // fallback (per spec): empty/invalid/earlier-than-teaser-start all
    // collapse to teaserStart itself (today's one-stage behaviour);
    // later-than-Friday clamps to Friday's start, so the weekend never
    // waits on a merchant typo — doors/widget just appear when it begins.
    var doorsStart = isValidDateShape(config.doorsStart) ? toComparable(config.doorsStart) : teaserStart;
    if (!doorsStart || (teaserStart && doorsStart < teaserStart)) doorsStart = teaserStart;
    if (dayStarts.fri && doorsStart > dayStarts.fri) doorsStart = dayStarts.fri;

    var phase;
    if (teaserStart && nowComparable < teaserStart) {
      phase = 'before';
    } else if (nowComparable < dayStarts.fri) {
      phase = 'teaser';
    } else if (nowComparable < dayStarts.sat) {
      phase = 'fri';
    } else if (nowComparable < dayStarts.sun) {
      phase = 'sat';
    } else if (nowComparable < dayStarts.mon) {
      phase = 'sun';
    } else if (nowComparable < weekendEnd) {
      phase = 'mon';
    } else {
      phase = 'ended';
    }

    var todayIndex = DAY_ORDER.indexOf(phase);
    var doorStates = {};
    DAY_ORDER.forEach(function (key, i) {
      if (phase === 'before' || phase === 'teaser') {
        doorStates[key] = 'locked';
      } else if (phase === 'ended') {
        doorStates[key] = 'gone';
      } else if (i < todayIndex) {
        doorStates[key] = 'gone';
      } else if (i === todayIndex) {
        doorStates[key] = 'open';
      } else {
        doorStates[key] = 'locked';
      }
    });

    // Upper boundary for whichever day is "today" — midnight tonight, or
    // weekendEnd for Monday, since that's genuinely its own end time.
    var upperBoundByKey = { fri: dayStarts.sat, sat: dayStarts.sun, sun: dayStarts.mon, mon: weekendEnd };

    var todayCountdownTarget = null;
    if (phase === 'teaser') {
      todayCountdownTarget = dayStarts.fri;
    } else if (todayIndex !== -1) {
      todayCountdownTarget = upperBoundByKey[phase];
    }

    var active = phase !== 'before' && phase !== 'ended';
    var isWeekend = todayIndex !== -1;
    // Stage 1 (teaserStart .. doorsStart): bar only. Stage 2 (doorsStart
    // onward, and the whole weekend regardless of the setting): bar +
    // doors + widget. `active` alone still gates the bar/teal-bar-swap/
    // bf-active skin exactly as before — this is the narrower, additional
    // gate just for the doors section and the floating widget.
    var doorsActive = active && (isWeekend || nowComparable >= doorsStart);

    return {
      phase: phase,
      active: active,
      isWeekend: isWeekend,
      doorsActive: doorsActive,
      doorStates: doorStates,
      dayStarts: dayStarts,
      barCountdownTarget: todayCountdownTarget,
    };
  }

  function updateBar(state, nowComparable) {
    var bar = document.querySelector('[data-bf-bar]');
    if (!bar) return;

    if (!state.active) {
      bar.hidden = true;
      return;
    }
    bar.hidden = false;
    bar.setAttribute('data-bf-phase', state.phase);

    var countdownEls = bar.querySelectorAll('[data-bf-bar-countdown]');
    if (countdownEls.length) {
      var text = 'Ends in ' + formatCountdown(countdownDelta(nowComparable, state.barCountdownTarget));
      countdownEls.forEach(function (el) {
        el.textContent = text;
      });
    }

    // Teaser-only countdown (both stages — doorsActive doesn't change
    // `phase`, so this keeps ticking the same way through stage 1 and
    // stage 2). Digits only: the "unlocks in" label is static, merchant-
    // editable HTML right next to it (sections/bf-announcement-bar.liquid),
    // not written by this script.
    var teaserCountdownEls = bar.querySelectorAll('[data-bf-teaser-countdown]');
    if (teaserCountdownEls.length) {
      var teaserText = formatCountdown(countdownDelta(nowComparable, state.barCountdownTarget));
      teaserCountdownEls.forEach(function (el) {
        el.textContent = teaserText;
      });
    }
  }

  function updateDoors(state, nowComparable) {
    var section = document.querySelector('[data-bf-doors]');
    if (!section) return;

    if (!state.doorsActive) {
      section.hidden = true;
      return;
    }
    section.hidden = false;
    section.setAttribute('data-bf-phase', state.phase);

    var doorsCountdownEl = section.querySelector('[data-bf-doors-countdown]');
    if (doorsCountdownEl) {
      if (state.phase === 'teaser') {
        doorsCountdownEl.hidden = false;
        doorsCountdownEl.textContent =
          'Unlocks in ' + formatCountdown(countdownDelta(nowComparable, state.dayStarts.fri));
      } else {
        doorsCountdownEl.hidden = true;
      }
    }

    DAY_ORDER.forEach(function (key) {
      var doorEl = section.querySelector('[data-bf-door="' + key + '"]');
      if (!doorEl) return;
      doorEl.setAttribute('data-state', state.doorStates[key]);

      if (state.doorStates[key] === 'locked') {
        var miniCountdownEl = doorEl.querySelector('[data-bf-door-countdown]');
        if (miniCountdownEl) {
          miniCountdownEl.textContent = formatCountdown(countdownDelta(nowComparable, state.dayStarts[key]));
        }
      }
    });
  }

  // Even once the widget is no longer waiting on the cookie banner, the
  // banner itself can still be genuinely on screen (its grace period is
  // about giving up waiting on it, not about it going away) — and its
  // z-index (9998) sits above the widget's, so without this the widget
  // would be "shown" per JS but completely hidden underneath the banner
  // in practice. Lifts the widget clear of the banner's current height
  // whenever that's the case; clears back to 0 the moment the banner
  // itself is gone, so the widget isn't left sitting unnecessarily high.
  function updateWidgetBannerClearance(widget) {
    var banner = document.querySelector('.cb-cookie_consent-container');
    if (banner) {
      var style = getComputedStyle(banner);
      if (style.display !== 'none' && style.visibility !== 'hidden' && parseFloat(style.opacity) !== 0) {
        var rect = banner.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          var clearance = Math.max(0, window.innerHeight - rect.top) + 12;
          widget.style.setProperty('--bf-cookie-clearance', clearance + 'px');
          return;
        }
      }
    }
    widget.style.removeProperty('--bf-cookie-clearance');
  }

  function updateWidget(state, nowComparable) {
    var widget = document.querySelector('[data-bf-widget]');
    var lightbox = document.querySelector('[data-bf-lightbox]');
    var reason = 'shown';

    if (!widget) {
      reason = 'not on this page';
    } else if (!state.doorsActive) {
      reason = 'hidden — not active (before doors start / after end)';
      widget.hidden = true;
    } else {
      var cookieStatus = cookieBannerStatus();
      if (cookieStatus.blocking) {
        reason = 'hidden — waiting for cookie banner';
        widget.hidden = true;
      } else {
        widget.hidden = false;
        widget.setAttribute('data-bf-phase', state.phase);
        updateWidgetBannerClearance(widget);
        var countdownEls = widget.querySelectorAll('[data-bf-widget-countdown]');
        if (countdownEls.length) {
          var text = formatCountdown(countdownDelta(nowComparable, state.barCountdownTarget));
          countdownEls.forEach(function (el) {
            el.textContent = text;
          });
        }
        reason = widget.getAttribute('data-bf-collapsed') === 'true' ? 'collapsed' : 'shown';
      }
    }

    if (lightbox) {
      lightbox.setAttribute('data-bf-phase', state.phase);
    }

    return reason;
  }

  function updateSkin(config, state) {
    document.documentElement.classList.toggle('bf-active', state.active);
    var skinOn = !!config.darkSkinEnabled && state.isWeekend;
    document.documentElement.classList.toggle('bf-weekend-skin', skinOn);

    // The overlay markup itself isn't rendered on the homepage at all
    // (layout/theme.liquid, request.page_type == 'index') — the dark hero
    // and doors already carry the mood there, and the smoke was tinting
    // the hero photo.
    if (!document.querySelector('.bf-smoke-overlay')) return 'off — homepage';
    var isMobile = false;
    try {
      isMobile = !window.matchMedia('(min-width: 750px)').matches;
    } catch (e) {}
    if (isMobile) return 'off — mobile';
    if (!skinOn) return 'off — not weekend';
    var reducedMotion = false;
    try {
      reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {}
    return reducedMotion ? 'on (static, reduced motion)' : 'on (animated)';
  }

  function updatePreviewLabel(preview, widgetReason, smokeStatus) {
    var existing = document.querySelector('.bf-preview-label');
    if (!preview) {
      if (existing) existing.remove();
      return;
    }
    if (!existing) {
      existing = document.createElement('div');
      existing.className = 'bf-preview-label';
      var line1 = document.createElement('div');
      line1.className = 'bf-preview-label__line';
      var line2 = document.createElement('div');
      line2.className = 'bf-preview-label__line bf-preview-label__line--diagnostic';
      existing.appendChild(line1);
      existing.appendChild(line2);
      document.body.appendChild(existing);
    }
    existing.querySelector('.bf-preview-label__line').textContent = 'PREVIEW: ' + preview.raw;
    existing.querySelector('.bf-preview-label__line--diagnostic').textContent =
      'widget: ' + widgetReason + ' · smoke: ' + smokeStatus;

    // Positioned just under the header's actual bottom edge every time —
    // computed fresh (not a fixed CSS value) because header height changes
    // (mobile 2-row layout, the purple bar appearing/disappearing), and
    // this is the only spot on the page that's never the full-width bar,
    // the bottom-left widget, or the bottom-right gift/insider widgets.
    var headerWrap = document.querySelector('.gw-header-wrap');
    if (headerWrap) {
      var bottom = headerWrap.getBoundingClientRect().bottom;
      existing.style.top = Math.max(8, Math.round(bottom) + 8) + 'px';
    }
  }

  function tick(config, preview) {
    var now = londonNowComparable(preview);
    var state = computeState(config, now);
    var smokeStatus = updateSkin(config, state);
    updateBar(state, now);
    updateDoors(state, now);
    var widgetReason = updateWidget(state, now);
    // Re-run every tick, not just once at startup — the header's height
    // (and so its bottom edge) changes as the purple bar appears/
    // disappears across phases, and the label needs to track it, plus the
    // diagnostic line needs to track live widget/smoke status.
    if (preview) updatePreviewLabel(preview, widgetReason, smokeStatus);
    return state;
  }

  // ---- Widget interactions (wired once, not on every tick) ----------------

  function wireWidget(getPhase) {
    var widget = document.querySelector('[data-bf-widget]');
    var lightbox = document.querySelector('[data-bf-lightbox]');

    if (widget) {
      var collapsed = false;
      try {
        collapsed = sessionStorage.getItem(COLLAPSED_STORAGE_KEY) === '1';
      } catch (e) {}
      widget.setAttribute('data-bf-collapsed', collapsed ? 'true' : 'false');

      var closeBtn = widget.querySelector('[data-bf-widget-close]');
      var reopenBtn = widget.querySelector('[data-bf-widget-reopen]');
      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          widget.setAttribute('data-bf-collapsed', 'true');
          try {
            sessionStorage.setItem(COLLAPSED_STORAGE_KEY, '1');
          } catch (e) {}
        });
      }
      if (reopenBtn) {
        reopenBtn.addEventListener('click', function () {
          widget.setAttribute('data-bf-collapsed', 'false');
          try {
            sessionStorage.setItem(COLLAPSED_STORAGE_KEY, '0');
          } catch (e) {}
        });
      }

      var openBtn = widget.querySelector('[data-bf-widget-open]');
      if (openBtn && lightbox) {
        openBtn.addEventListener('click', function () {
          lightbox.setAttribute('data-bf-phase', getPhase());
          lightbox.hidden = false;
        });
      }
    }

    if (lightbox) {
      var closeLightbox = function () {
        lightbox.hidden = true;
      };
      var closeBtnEl = lightbox.querySelector('[data-bf-lightbox-close]');
      if (closeBtnEl) closeBtnEl.addEventListener('click', closeLightbox);
      // "Click outside" — the backdrop is a ::before pseudo-element on
      // .bf-lightbox itself (never an empty div), so a click lands on the
      // outer .bf-lightbox exactly when it's NOT on the panel or its
      // children (event.target === lightbox only for clicks that hit the
      // backdrop area, since .bf-lightbox__panel is a real element that
      // captures its own clicks).
      lightbox.addEventListener('click', function (e) {
        if (e.target === lightbox) closeLightbox();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !lightbox.hidden) closeLightbox();
      });
    }
  }

  // ---- Smoke texture: baked once via canvas, then just moved -------------
  //
  // Round 7 rendered the smoke as a LIVE SVG feTurbulence filter on a full-
  // viewport masked layer, which the browser had to keep re-rasterising and
  // re-compositing on every scroll frame — confirmed the actual cause of
  // the reported lag via a before/after Chrome trace (see round 8 report).
  // This renders the exact same feTurbulence + feColorMatrix noise, but
  // only ONCE, into an offscreen <canvas>, exports it as a plain PNG data
  // URL, and caches that in sessionStorage so no later page in the visit
  // regenerates it. From then on it's just a background-image on small
  // per-edge strips, animated with nothing but `transform: translate` —
  // no live filter, no full-viewport mask, on the page at all.
  var SMOKE_TEXTURE_KEY = 'bfSmokeTextureV1';
  var SMOKE_TEXTURE_SIZE = 640;

  function bakeSmokeTexture(callback) {
    var cached = null;
    try {
      cached = sessionStorage.getItem(SMOKE_TEXTURE_KEY);
    } catch (e) {}
    if (cached) {
      callback(cached);
      return;
    }

    var svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="' + SMOKE_TEXTURE_SIZE + '" height="' + SMOKE_TEXTURE_SIZE + '">' +
      '<filter id="n" x="-20%" y="-20%" width="140%" height="140%" color-interpolation-filters="sRGB">' +
      '<feTurbulence type="fractalNoise" baseFrequency="0.010 0.016" numOctaves="4" seed="7"></feTurbulence>' +
      '<feColorMatrix type="matrix" values="0 0 0 0 0.13  0 0 0 0 0.09  0 0 0 0 0.19  0 0 0 1.15 0"></feColorMatrix>' +
      '</filter>' +
      '<rect width="100%" height="100%" filter="url(#n)"></rect>' +
      '</svg>';

    var img = new Image();
    img.onload = function () {
      try {
        var canvas = document.createElement('canvas');
        canvas.width = SMOKE_TEXTURE_SIZE;
        canvas.height = SMOKE_TEXTURE_SIZE;
        var ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, SMOKE_TEXTURE_SIZE, SMOKE_TEXTURE_SIZE);
        var dataUrl = canvas.toDataURL('image/png');
        try {
          sessionStorage.setItem(SMOKE_TEXTURE_KEY, dataUrl);
        } catch (e) {}
        callback(dataUrl);
      } catch (e) {
        callback(null);
      }
    };
    img.onerror = function () {
      callback(null);
    };
    img.src = 'data:image/svg+xml;base64,' + window.btoa(svg);
  }

  function applySmokeTexture(overlay, dataUrl) {
    if (!dataUrl) return;
    var textures = overlay.querySelectorAll('.bf-smoke-strip__texture');
    for (var i = 0; i < textures.length; i++) {
      textures[i].style.backgroundImage = 'url(' + dataUrl + ')';
    }
  }

  // Pauses the (already GPU-cheap) drift animation entirely while the tab
  // is hidden or while the visitor is actively scrolling, resuming a beat
  // after scrolling stops — belt-and-suspenders on top of the texture bake
  // itself, so there's never any animation work competing with scroll.
  function wireSmokePerformance(overlay) {
    var scrollTimer = null;

    function setPaused(paused) {
      overlay.classList.toggle('bf-smoke-overlay--paused', paused);
    }

    document.addEventListener('visibilitychange', function () {
      setPaused(document.hidden);
    });

    window.addEventListener(
      'scroll',
      function () {
        setPaused(true);
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          if (!document.hidden) setPaused(false);
        }, 200);
      },
      { passive: true }
    );
  }

  function init() {
    var config = readConfig();
    if (!config) return;

    var preview = resolvePreviewOverride();
    updatePreviewLabel(preview, '…', '…');

    var smokeOverlay = document.querySelector('.bf-smoke-overlay');
    if (smokeOverlay) {
      // Round 9: no smoke on mobile at all — assets/bf-weekend.css already
      // force-hides .bf-smoke-overlay with display:none below 750px, but
      // that alone doesn't stop this file wastefully baking a canvas
      // texture nothing will show. Gated behind the same breakpoint the
      // theme's own header uses, with a listener so rotating a phone into
      // landscape (or resizing a desktop window down into a tablet split-
      // view and back) bakes it exactly once, the first time it's actually
      // needed, rather than never or repeatedly.
      var desktopMql = null;
      try {
        desktopMql = window.matchMedia('(min-width: 750px)');
      } catch (e) {}

      var smokeTextureBaked = false;
      var ensureSmokeTexture = function () {
        if (smokeTextureBaked) return;
        if (desktopMql && !desktopMql.matches) return;
        smokeTextureBaked = true;
        bakeSmokeTexture(function (dataUrl) {
          applySmokeTexture(smokeOverlay, dataUrl);
        });
      };

      ensureSmokeTexture();
      wireSmokePerformance(smokeOverlay);

      if (desktopMql) {
        if (desktopMql.addEventListener) {
          desktopMql.addEventListener('change', ensureSmokeTexture);
        } else if (desktopMql.addListener) {
          desktopMql.addListener(ensureSmokeTexture); // older Safari
        }
      }
    }

    var latestPhase = 'before';
    wireWidget(function () {
      return latestPhase;
    });

    var runTick = function () {
      var state = tick(config, preview);
      latestPhase = state.phase;
    };
    runTick();
    setInterval(runTick, TICK_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
